import type {ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {Grid3x3, Mic, MicOff, MonitorUp, Pause, Play, SlidersHorizontal, UserPlus, Video, VideoOff, Volume2} from 'lucide-react';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {groupSurfaceMessages as gs} from '../group/groupMessages';
import type {ControlSpec} from '../group/useGroupControls';
import type {MatrixCallLeg} from '../model/types';
import {hasLiveVideo, type MatrixCallMedia} from './useMatrixCallMedia';
import {matrixActions} from './matrixActions';
import {matrixSurfaceMessages as m} from './matrixMessages';

interface MatrixControlOptions {
    readonly iconSize: number;
    readonly onKeypad: () => void;
    readonly onSpeaker: () => void;
    readonly onDevices: () => void;
    readonly onAddCall?: () => void;
}

export interface MatrixControls {
    readonly microphone: ControlSpec;
    readonly keypad?: ControlSpec;
    readonly speaker: ControlSpec;
    readonly hold: ControlSpec;
    readonly camera?: ControlSpec;
    readonly videoCta?: ControlSpec;
    readonly share?: ControlSpec;
    readonly addCall?: ControlSpec;
    readonly devices: ControlSpec;
}

export const useMatrixControls = (leg: MatrixCallLeg, media: MatrixCallMedia, options: MatrixControlOptions): MatrixControls => {
    const intl = useIntl();
    const icon = (node: (size: number) => ReactNode) => node(options.iconSize);
    const reconnecting = leg.network === 'reconnecting';
    const cameraOn = hasLiveVideo(media.feeds.localUsermedia);
    const sharing = !!media.feeds.localScreen;

    const microphone: ControlSpec = {
        id: 'microphone',
        testId: 'one-to-one-mic',
        icon: leg.muted ? icon(size => <MicOff size={size} aria-hidden="true"/>) : icon(size => <Mic size={size} aria-hidden="true"/>),
        label: intl.formatMessage(leg.muted ? gm.controlUnmute : gm.controlMute),
        caption: intl.formatMessage(leg.muted ? gs.captionUnmute : gs.captionMute),
        tone: leg.muted ? 'pressed' : 'default',
        disabled: reconnecting,
        onClick: matrixActions.toggleMute,
    };

    const keypad: ControlSpec | undefined = leg.capabilities.has('keypad')
        ? {
              id: 'keypad',
              testId: 'one-to-one-keypad',
              icon: icon(size => <Grid3x3 size={size} aria-hidden="true"/>),
              label: intl.formatMessage(m.keypadAria),
              caption: intl.formatMessage(m.captionKeypad),
              tone: 'default',
              disabled: reconnecting,
              onClick: options.onKeypad,
          }
        : undefined;

    const speaker: ControlSpec = {
        id: 'speaker',
        testId: 'one-to-one-speaker',
        icon: icon(size => <Volume2 size={size} aria-hidden="true"/>),
        label: intl.formatMessage(m.speakerAria),
        caption: intl.formatMessage(m.captionSpeaker),
        tone: 'default',
        disabled: reconnecting,
        onClick: options.onSpeaker,
    };

    const hold: ControlSpec = {
        id: 'hold',
        testId: 'one-to-one-hold',
        icon: leg.held ? icon(size => <Play size={size} aria-hidden="true"/>) : icon(size => <Pause size={size} aria-hidden="true"/>),
        label: intl.formatMessage(leg.held ? m.resumeAria : m.holdAria),
        caption: intl.formatMessage(leg.held ? m.captionResume : m.captionHold),
        tone: leg.held ? 'pressed' : 'default',
        disabled: reconnecting,
        onClick: () => matrixActions.setHold(!leg.held),
    };

    const camera: ControlSpec | undefined = leg.capabilities.has('camera')
        ? {
              id: 'camera',
              testId: 'one-to-one-camera',
              icon: cameraOn ? icon(size => <Video size={size} aria-hidden="true"/>) : icon(size => <VideoOff size={size} aria-hidden="true"/>),
              label: intl.formatMessage(cameraOn ? gm.controlCameraOff : gm.controlCameraOn),
              caption: intl.formatMessage(gs.captionCamera),
              tone: cameraOn ? 'pressed' : 'default',
              disabled: reconnecting || leg.held,
              onClick: () => matrixActions.setCamera(!cameraOn),
          }
        : undefined;

    const videoCta: ControlSpec | undefined = camera && !cameraOn
        ? {...camera, id: 'video-cta', caption: intl.formatMessage(m.captionVideo), tone: 'brand', icon: icon(size => <Video size={size} aria-hidden="true"/>)}
        : undefined;

    const share: ControlSpec | undefined = leg.capabilities.has('share')
        ? {
              id: 'share',
              testId: 'one-to-one-screen-share',
              icon: icon(size => <MonitorUp size={size} aria-hidden="true"/>),
              label: intl.formatMessage(sharing ? gm.controlStopSharing : gm.controlShareScreen),
              caption: intl.formatMessage(gs.captionShare),
              tone: sharing ? 'pressed' : 'default',
              disabled: reconnecting || leg.held,
              onClick: () => matrixActions.setScreenShare(!sharing),
          }
        : undefined;

    const devices: ControlSpec = {
        id: 'devices',
        testId: 'one-to-one-settings',
        icon: icon(size => <SlidersHorizontal size={size} aria-hidden="true"/>),
        label: intl.formatMessage(gs.deviceSettings),
        caption: intl.formatMessage(gs.captionDevices),
        tone: 'default',
        hasPopup: 'dialog',
        onClick: options.onDevices,
    };

    const addCall: ControlSpec | undefined = leg.capabilities.has('addPeople') && leg.phase === 'active' && options.onAddCall
        ? {
              id: 'add-call',
              testId: 'one-to-one-add-call',
              icon: icon(size => <UserPlus size={size} aria-hidden="true"/>),
              label: intl.formatMessage(m.addCallAria),
              caption: intl.formatMessage(gs.captionAdd),
              tone: 'default',
              disabled: reconnecting || leg.held,
              onClick: options.onAddCall,
          }
        : undefined;

    return {microphone, keypad, speaker, hold, camera, videoCta, share, addCall, devices};
};
